import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { showApiError, showSuccess } from "@/lib/toast";
import { getTag } from "@/sideEffects/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { TagIcon } from "lucide-react";
import { AppMultiSelectCommand } from "@/components/ui/app-multi-select-command";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { useState, useEffect } from "react";

interface Props {
  addFn: (tagIntId: number) => Promise<unknown>;
  deleteFn: (entityTagId: string) => Promise<unknown>;
  entityAssignedTagIntIds: { tagIntId: number; id: string }[];
  onUpdate: () => void;
}

export const EntityTagsUpdatePopover: React.FC<Props> = ({ addFn, deleteFn, entityAssignedTagIntIds, onUpdate }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  const addMutation = useMutation({
    mutationFn: (tagIntId: number) => addFn(tagIntId),
    onSuccess: () => {
      showSuccess(t("common.updatedSuccess"));
      onUpdate();
    },
    onError: showApiError,
  });

  const deleteMutation = useMutation({
    mutationFn: (entityTagId: string) => deleteFn(entityTagId),
    onSuccess: () => {
      showSuccess(t("common.updatedSuccess"));
      onUpdate();
    },
    onError: showApiError,
  });

  const { data: allTags, isLoading: allTagsLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => getTag({ numberOfObjectsPerPage: 999999, pageNumber: 1 }, { Firm_Id: String(activeFirm?.firmGuid) }),
  });

  const allTagsData = allTags?.data?.result || [];
  const options = allTagsData.map((tag) => ({
    value: tag.intId,
    label: tag.name,
  }));

  const selectedValues = entityAssignedTagIntIds.map((tag) => tag.tagIntId);

  // Initialize selectedTags
  useEffect(() => {
    setSelectedTags(selectedValues);
  }, []);

  const handleChange = (newValues: (string | number)[]) => {
    setSelectedTags(newValues as number[]);
  };

  const handleApply = () => {
    const currentSet = new Set(selectedValues);
    const newSet = new Set(selectedTags);

    // Handle removals
    selectedValues.forEach((value) => {
      if (!newSet.has(value)) {
        const tagToRemove = entityAssignedTagIntIds.find((tag) => tag.tagIntId === value);
        if (tagToRemove) {
          console.log("tagToRemove", tagToRemove);
          deleteMutation.mutate(tagToRemove.id);
        }
      }
    });

    // Handle additions
    selectedTags.forEach((value) => {
      if (!currentSet.has(value)) {
        addMutation.mutate(value);
      }
    });
  };

  const isLoading = addMutation.isPending || deleteMutation.isPending || allTagsLoading;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-6" size="sm">
          <TagIcon className="h-4 w-4" />
          {t("common.tags")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <LoadingOverlay isLoading={isLoading}>
          <div className="flex flex-col gap-2">
            <AppMultiSelectCommand
              value={selectedTags}
              onChange={handleChange}
              options={options}
              isLoading={isLoading}
              placeholder={t("common.tags")}
              label={t("common.tags")}
            />
            <div className="p-2">
              <Button onClick={handleApply} className="w-full" variant="outline" disabled={isLoading}>
                {t("common.update")}
              </Button>
            </div>
          </div>
        </LoadingOverlay>
      </PopoverContent>
    </Popover>
  );
};
